import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import axios from 'axios'

// Configure axios defaults - Use environment variable for API URL
// When VITE_API_URL is empty, use relative path (nginx handles routing)
const API_URL = import.meta.env.VITE_API_URL || ''
axios.defaults.baseURL = API_URL ? `${API_URL}/api/admin` : '/api/admin'

console.log('[AuthStore] VITE_API_URL:', import.meta.env.VITE_API_URL)
console.log('[AuthStore] Axios baseURL:', axios.defaults.baseURL)

// Add token to requests if available
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Handle 401 responses
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on verify-token failures (let authStore handle it)
    const isVerifyToken = error.config?.url?.includes('/auth/verify-token')

    if (error.response?.status === 401 && !isVerifyToken) {
      // Clear auth and redirect to login
      localStorage.removeItem('token')
      localStorage.removeItem('admin')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      admin: null,
      isAuthenticated: false,
      loading: false,
      error: null,

      login: async (email, password) => {
        console.log('[AuthStore] Login attempt:', email)
        console.log('[AuthStore] Request URL will be:', axios.defaults.baseURL + '/auth/login')
        set({ loading: true, error: null })
        try {
          console.log('[AuthStore] Sending login request...')
          const response = await axios.post('/auth/login', { email, password })
          console.log('[AuthStore] Login response received:', response.status)
          const { access_token, admin } = response.data

          localStorage.setItem('token', access_token)
          localStorage.setItem('admin', JSON.stringify(admin))

          set({
            token: access_token,
            admin,
            isAuthenticated: true,
            loading: false,
            error: null
          })

          console.log('[AuthStore] Login successful, redirecting...')
          return { success: true }
        } catch (error) {
          console.error('[AuthStore] Login failed:', error.message, error.response?.status)
          set({
            loading: false,
            error: error.response?.data?.detail || 'Login failed'
          })
          return { success: false, error: error.response?.data?.detail || 'Login failed' }
        }
      },

      logout: async () => {
        try {
          await axios.post('/auth/logout')
        } catch (error) {
          console.error('Logout error:', error)
        } finally {
          localStorage.removeItem('token')
          localStorage.removeItem('admin')
          set({
            token: null,
            admin: null,
            isAuthenticated: false,
            error: null
          })
        }
      },

      verifyToken: async () => {
        const token = localStorage.getItem('token')
        if (!token) {
          set({ isAuthenticated: false })
          return false
        }

        try {
          // Add timeout to prevent infinite hanging
          const controller = new AbortController()
          const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

          const response = await axios.post('/auth/verify-token', {}, {
            signal: controller.signal
          })

          clearTimeout(timeoutId)

          if (response.data.valid) {
            const admin = JSON.parse(localStorage.getItem('admin') || '{}')
            set({
              token,
              admin,
              isAuthenticated: true
            })
            return true
          }
        } catch (error) {
          console.log('Token verification failed:', error.message)
          localStorage.removeItem('token')
          localStorage.removeItem('admin')
          set({
            token: null,
            admin: null,
            isAuthenticated: false
          })
          return false
        }
      },

      changePassword: async (currentPassword, newPassword) => {
        try {
          await axios.post('/auth/change-password', {
            current_password: currentPassword,
            new_password: newPassword
          })
          return { success: true }
        } catch (error) {
          return {
            success: false,
            error: error.response?.data?.detail || 'Failed to change password'
          }
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        admin: state.admin,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)

export default useAuthStore
